import { useEffect, useRef, useState } from 'react';

import { canEnterStep } from '../domain/store.js';
import type { StepId } from '../domain/types.js';
import { STEP_LABELS, STEP_ORDER } from '../domain/types.js';
import {
  AccentStrip,
  AppearanceControl,
  AppearanceProvider,
} from './Appearance.js';
import { useOpenLicenseDialog, useProjectState, useStore } from './hooks.js';
import { Step1Upload } from './Step1Upload.js';
import { Step2Configure } from './Step2Configure.js';
import { Step3Results } from './Step3Results.js';
import { Step4Diagnostics } from './Step4Diagnostics.js';
import { Wordmark } from './Wordmark.js';

/**
 * Step header: the step's title set big with its subtitle.
 * The words are STEP_LABELS verbatim — presentation only.
 * (Cross-Studio parity phase, 2026-10-30: the 2026-10-18
 * redesign's large Signal Orange numeral was removed — the
 * owner's standing rule bans big numerals as the visual
 * differentiator for steps; the title and subtitle carry
 * the head, and the step bar's labeled chips carry the
 * position.)
 */
function StepHead({ step }: { step: StepId }) {
  const label = STEP_LABELS[step];
  return (
    <div className="step-head">
      <div>
        <h2 className="step-head-title">{label.title}</h2>
        <p className="step-head-sub">{label.sub}</p>
      </div>
    </div>
  );
}

/** The app shell exactly as frozen: head, step bar, four panels. */
export function Shell() {
  const store = useStore();
  const state = useProjectState();
  const openLicenseDialog = useOpenLicenseDialog();
  const [saved, setSaved] = useState(false);
  const licensed = state.license.status === 'licensed';

  // VW-198: at phone widths the steps bar is one
  // scrollable row; keep the active tab fully in view
  // when the step changes. "nearest" is a no-op when
  // it is already visible.
  const stepsRef = useRef<HTMLElement | null>(null);
  useEffect(() => {
    stepsRef.current
      ?.querySelector('.step.active')
      ?.scrollIntoView({ block: 'nearest', inline: 'nearest' });
  }, [state.step]);

  const areas = state.setup?.assessment.summary.areasFound ?? null;
  const subtitleParts: string[] = [];
  if (state.source.filename !== null) subtitleParts.push(state.source.filename);
  if (areas !== null) subtitleParts.push(`${areas} areas`);
  subtitleParts.push(
    state.config.mode === 'quick' ? 'Quick mode' : 'Full mode',
  );

  const save = async () => {
    await store.flushAutosave();
    setSaved(true);
    setTimeout(() => setSaved(false), 2000);
  };

  return (
    <section className="shell" aria-label="SAE Studio">
      <AppearanceProvider>
      <div className="shell-head">
        <div className="shell-head-left">
          <span className="dotgrid" aria-hidden="true">
            <i></i>
            <i></i>
            <i></i>
            <i></i>
            <i></i>
            <i></i>
            <i></i>
            <i></i>
            <i></i>
          </span>
          <div>
            <h1 className="shell-title">
              <Wordmark /> — {state.projectName}
            </h1>
            <div className="shell-sub">{subtitleParts.join(' · ')}</div>
          </div>
        </div>
        <div className="head-actions">
          <AppearanceControl />
          <AccentStrip />
          <span className="badge">
            {licensed ? 'LICENSED' : 'TRIAL PREVIEW · EXPORTS LOCKED'}
          </span>
          <button className="btn" onClick={() => void save()}>
            {saved ? 'Saved' : 'Save Project'}
          </button>
          {licensed ? (
            <button className="btn" onClick={openLicenseDialog}>
              {state.license.email ?? 'License'}
            </button>
          ) : (
            <button className="btn btn-orange" onClick={openLicenseDialog}>
              Unlock the Tool
            </button>
          )}
        </div>
      </div>

      <nav className="steps" aria-label="Workflow steps" ref={stepsRef}>
        {STEP_ORDER.map((stepId: StepId, i) => {
          // Free navigation (owner-approved 2026-10-18): every tab is
          // always clickable for inspection. canEnterStep now only
          // marks the steps whose work actually exists (the "done"
          // indicator); a step without content renders its empty state.
          const ready = canEnterStep(state, stepId);
          return (
            <button
              key={stepId}
              className={`step${state.step === stepId ? ' active' : ''}${ready ? ' done' : ''}`}
              aria-current={state.step === stepId ? 'step' : undefined}
              onClick={() => store.goToStep(stepId)}
            >
              <b>
                <span className="step-num">{i + 1}</span> {STEP_LABELS[stepId].title}
              </b>
              <span>{STEP_LABELS[stepId].sub}</span>
            </button>
          );
        })}
      </nav>

      <main className="panels">
        <StepHead step={state.step} />
        {state.step === 'upload' && <Step1Upload />}
        {state.step === 'configure' && <Step2Configure />}
        {state.step === 'results' && <Step3Results />}
        {state.step === 'diagnostics' && <Step4Diagnostics />}
      </main>
      </AppearanceProvider>
    </section>
  );
}
